import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const AdministrationIntegrations = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [integrations, setIntegrations] = useState([]);
  const [stats, setStats] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [integrationForm, setIntegrationForm] = useState({
    integration_name: '',
    integration_type: 'other',
    api_key: '',
    api_secret: '',
    webhook_url: ''
  });

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  const integrationIcons = {
    payment: '💳',
    sms: '📱',
    email: '📧',
    cloud: '☁️',
    other: '🔗'
  };

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([fetchIntegrations(), fetchStats()]);
    setLoading(false);
  };

  const fetchIntegrations = async () => {
    try {
      const data = await apiGet('/admin/administration/integrations');
      if (data.success) setIntegrations(data.data);
    } catch (error) {
      console.error('Error fetching integrations:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await apiGet('/admin/administration/integrations/stats');
      if (data.success) setStats(data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSubmitIntegration = async () => {
    if (!integrationForm.integration_name) {
      alert('Please enter integration name');
      return;
    }

    try {
      const url = editingIntegration
        ? `${PRODUCTION_URL}/api/admin/administration/integrations/${editingIntegration.id}`
        : `${PRODUCTION_URL}/api/admin/administration/integrations`;

      const response = await fetch(url, {
        method: editingIntegration ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(integrationForm)
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(`✅ Integration ${editingIntegration ? 'updated' : 'created'} successfully!`);
        setShowModal(false);
        setEditingIntegration(null);
        setIntegrationForm({ integration_name: '', integration_type: 'other', api_key: '', api_secret: '', webhook_url: '' });
        fetchAllData();
      }
    } catch (error) {
      console.error('Error saving integration:', error);
      alert('Error saving integration');
    }
  };

  const handleEditIntegration = async (integration) => {
    setEditingIntegration(integration);

    const data = await apiGet('/admin/administration/integrations/${integration.id}');

    if (data.success) {
      const int = data.data;
      setIntegrationForm({
        integration_name: int.integration_name,
        integration_type: int.integration_type,
        api_key: int.api_key || '',
        api_secret: '', // Don't show secret
        webhook_url: int.webhook_url || ''
      });
      setShowModal(true);
    }
  };

  const handleToggleIntegration = async (integrationId, currentStatus) => {
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/administration/integrations/${integrationId}/toggle`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !currentStatus })
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(data.message);
        fetchAllData();
      }
    } catch (error) {
      console.error('Error toggling integration:', error);
    }
  };

  const handleTestConnection = async (integrationId) => {
    try {
      const data = await apiPost('/admin/administration/integrations/${integrationId}/test', {});
      if (data.success && data.data.verified) {
        setSuccessMessage('✅ Connection test successful!');
        fetchAllData();
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      alert('Error testing connection');
    }
  };

  const handleDeleteIntegration = async (integrationId) => {
    if (!window.confirm('Are you sure you want to delete this integration?')) return;

    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/administration/integrations/${integrationId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage('✅ Integration deleted successfully!');
        fetchAllData();
      }
    } catch (error) {
      console.error('Error deleting integration:', error);
    }
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : isTablet ? '20px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : isTablet ? '24px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
      gap: isMobile ? '12px' : '20px',
      marginBottom: isMobile ? '20px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    statCard: {
      background: colors.white,
      padding: isMobile ? '20px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '20px' : isTablet ? '26px' : '32px',
      borderRadius: isMobile ? '16px' : isTablet ? '18px' : '20px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px 16px 4px' : '0 0 24px 0'
    },
    sectionTitle: {
      fontSize: isMobile ? '18px' : isTablet ? '20px' : '24px',
      fontWeight: '800',
      color: colors.textDark,
      marginBottom: isMobile ? '16px' : isTablet ? '20px' : '24px',
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '8px' : '12px',
      justifyContent: 'space-between'
    },
    button: {
      padding: isMobile ? '8px 14px' : '10px 18px',
      borderRadius: '8px',
      border: 'none',
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: colors.primary,
      color: colors.white
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '20px' : '40px'
    },
    modalContent: {
      background: colors.white,
      borderRadius: isMobile ? '16px' : '20px',
      padding: isMobile ? '20px' : '32px',
      maxWidth: isMobile ? '100%' : '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      marginBottom: '12px',
      fontFamily: 'inherit'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading integrations...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {successMessage && (
        <div style={{
          padding: isMobile ? '12px 16px' : '14px 20px',
          borderRadius: '10px',
          marginBottom: '16px',
          fontSize: isMobile ? '13px' : '14px',
          fontWeight: '600',
          background: 'rgba(74, 124, 42, 0.15)',
          color: colors.success,
          border: `2px solid ${colors.success}`,
          margin: isMobile ? '0 4px 12px 4px' : '0 0 16px 0'
        }}>
          {successMessage}
        </div>
      )}

      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Integrations Administration</h1>
        <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
          {isMobile ? 'APIs & services' : 'Manage third-party integrations, APIs, and external services'}
        </p>
      </div>

      <div style={styles.statsGrid}>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.primary}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', marginBottom: '8px', color: colors.primary }}>
            {stats.total_integrations || 0}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Total Integrations
          </div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.success}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', marginBottom: '8px', color: colors.success }}>
            {stats.active_integrations || 0}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Active
          </div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.accent}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', marginBottom: '8px', color: colors.accent }}>
            {stats.verified_integrations || 0}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Verified
          </div>
        </div>
      </div>

      <div style={styles.contentCard}>
        <div style={styles.sectionTitle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🔗</span>
            <span>Integrations & APIs</span>
          </div>
          <button onClick={() => { setEditingIntegration(null); setIntegrationForm({ integration_name: '', integration_type: 'other', api_key: '', api_secret: '', webhook_url: '' }); setShowModal(true); }} style={styles.button}>
            + Add Integration
          </button>
        </div>

        {integrations.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: colors.textLight }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚙️</div>
            <div>No integrations configured yet</div>
            <div style={{ marginTop: '8px', fontSize: '12px', opacity: 0.7 }}>
              Add your first integration to get started
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '16px' }}>
            {integrations.map(integration => (
              <div key={integration.id} style={{
                padding: isMobile ? '16px' : '20px',
                background: colors.lightGray,
                borderRadius: '12px',
                border: `2px solid ${integration.is_active ? colors.success : colors.mediumGray}`,
                position: 'relative'
              }}>
                <div style={{ position: 'absolute', top: '12px', right: '12px', fontSize: '32px' }}>
                  {integrationIcons[integration.integration_type] || '🔗'}
                </div>

                <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '700', marginBottom: '6px', color: colors.primary, paddingRight: '40px' }}>
                  {integration.integration_name}
                </div>

                <div style={{ fontSize: '11px', color: colors.textLight, marginBottom: '12px', textTransform: 'uppercase' }}>
                  {integration.integration_type}
                </div>

                <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontSize: '10px',
                    fontWeight: '600',
                    background: integration.is_active ? `${colors.success}20` : `${colors.danger}20`,
                    color: integration.is_active ? colors.success : colors.danger
                  }}>
                    {integration.is_active ? '✓ Active' : '✕ Inactive'}
                  </span>
                  {integration.is_verified && (
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '10px',
                      fontWeight: '600',
                      background: `${colors.accent}20`,
                      color: colors.accent
                    }}>
                      ✓ Verified
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => handleToggleIntegration(integration.id, integration.is_active)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: 'none',
                      fontSize: '10px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      background: integration.is_active ? colors.warning : colors.success,
                      color: colors.white
                    }}
                  >
                    {integration.is_active ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    onClick={() => handleTestConnection(integration.id)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: 'none',
                      fontSize: '10px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      background: colors.accent,
                      color: colors.white
                    }}
                  >
                    🔍 Test
                  </button>
                  <button
                    onClick={() => handleEditIntegration(integration)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: 'none',
                      fontSize: '10px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      background: colors.primary,
                      color: colors.white
                    }}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => handleDeleteIntegration(integration.id)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: 'none',
                      fontSize: '10px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      background: colors.danger,
                      color: colors.white
                    }}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div style={styles.modal} onClick={() => setShowModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: isMobile ? '18px' : '22px', marginBottom: '20px', fontWeight: '700' }}>
              {editingIntegration ? 'Edit Integration' : 'Add New Integration'}
            </h3>

            <input
              type="text"
              placeholder="Integration Name *"
              value={integrationForm.integration_name}
              onChange={(e) => setIntegrationForm({ ...integrationForm, integration_name: e.target.value })}
              style={styles.input}
            />

            <select
              value={integrationForm.integration_type}
              onChange={(e) => setIntegrationForm({ ...integrationForm, integration_type: e.target.value })}
              style={{ ...styles.input, background: colors.white }}
            >
              <option value="payment">💳 Payment Gateway</option>
              <option value="sms">📱 SMS Service</option>
              <option value="email">📧 Email Service</option>
              <option value="cloud">☁️ Cloud Storage</option>
              <option value="other">🔗 Other</option>
            </select>

            <input
              type="text"
              placeholder="API Key"
              value={integrationForm.api_key}
              onChange={(e) => setIntegrationForm({ ...integrationForm, api_key: e.target.value })}
              style={styles.input}
            />

            <input
              type="password"
              placeholder="API Secret"
              value={integrationForm.api_secret}
              onChange={(e) => setIntegrationForm({ ...integrationForm, api_secret: e.target.value })}
              style={styles.input}
            />

            <input
              type="url"
              placeholder="Webhook URL (optional)"
              value={integrationForm.webhook_url}
              onChange={(e) => setIntegrationForm({ ...integrationForm, webhook_url: e.target.value })}
              style={styles.input}
            />

            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button
                onClick={handleSubmitIntegration}
                style={{ ...styles.button, flex: 1 }}
              >
                {editingIntegration ? 'Update Integration' : 'Add Integration'}
              </button>
              <button
                onClick={() => setShowModal(false)}
                style={{ ...styles.button, background: colors.mediumGray, color: colors.textDark, flex: 1 }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdministrationIntegrations;